/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React, { useMemo } from 'react';
import ThemedButton from '../common/buttons/Button';
import { useAppSelector } from '@/hooks/useRedux';
import { asset } from '@/utils/runtimeOrg';
import {
  HiOutlineArrowRightOnRectangle,
  HiOutlineUserPlus,
  HiOutlineUserCircle,
  HiOutlineSquares2X2,
} from 'react-icons/hi2';

type HeroProps = {
  cfg: {
    heroSection?: { image?: string; title?: string; description?: string };
  };
};

export default function Hero({ cfg }: HeroProps) {
  const isLoggedIn = useAppSelector((s: any) =>
    Boolean(s?.auth?.signin?.token?.access)
  );

  const hero = cfg?.heroSection;

  const bgStyle = useMemo(() => {
    if (!hero?.image) return undefined;
    return { backgroundImage: `url('${asset(hero.image)}')` };
  }, [hero?.image]);

  if (!hero) {
    return (
      <section className="h-[50vh] md:h-[80vh] bg-[var(--color-background)]" />
    );
  }

  const { title, description } = hero;

  return (
    <section
      className="
        relative
       min-h-[50vh] md:min-h-[80vh]
        max-h-[80vh]
        bg-cover bg-center bg-no-repeat
        flex items-center
        overflow-hidden
      "
      style={bgStyle}
    >
      {/* Overlay for white-text readability across all breakpoints */}
      <div className="absolute inset-0 bg-linear-to-r from-black/55 via-black/35 to-black/15 md:from-black/50 md:via-black/25 md:to-black/5" />

      {/* Content */}
      <div className="relative z-10 w-full">
        <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-14 h-full flex items-center">
          <div className="w-full max-w-[clamp(20rem,52vw,48rem)] text-left">
            <p
              className="
                capitalize tracking-[-0.015em]
                text-white drop-shadow-sm
                text-[clamp(1.35rem,4.6vw,2.85rem)]
                leading-[clamp(1.75rem,5.2vw,3.25rem)]
                mb-[clamp(0.5rem,1.2vw,1rem)]
              "
              style={{
                fontFamily: 'Inter, var(--font-inter), AGTextXLRegular',
              }}
            >
              {title}
            </p>
            <p
              className="
                text-white drop-shadow-sm
                text-[clamp(0.95rem,2.4vw,1.45rem)]
                leading-[clamp(1.35rem,3vw,1.9rem)]
                max-w-[clamp(16rem,46vw,44rem)]
                mb-[clamp(1rem,2vw,2rem)]
              "
              style={{
                fontFamily: 'Inter, var(--font-inter), AGTextXLRegular',
              }}
            >
              {description}
            </p>
            {/* Buttons */}
            <div className="flex gap-3 sm:gap-4 flex-wrap items-center justify-start">
              {isLoggedIn ? (
                <>
                  <ThemedButton
                    variant="solid"
                    theme="accent"
                    text="My Account"
                    href="/account"
                    icon={<HiOutlineUserCircle />}
                    style={{
                      backgroundColor: '#ffffff',
                      color: 'var(--color-accent)',
                      borderColor: '#ffffff',
                    }}
                  />
                  <ThemedButton
                    theme="primary"
                    text="Explore Services"
                    href="/#services"
                    icon={<HiOutlineSquares2X2 />}
                  />
                </>
              ) : (
                <>
                  <ThemedButton
                    variant="solid"
                    theme="accent"
                    text="Log In"
                    href="/account/signin"
                    icon={<HiOutlineArrowRightOnRectangle />}
                    style={{
                      backgroundColor: '#ffffff',
                      color: 'var(--color-accent)',
                      borderColor: '#ffffff',
                    }}
                  />
                  <ThemedButton
                    theme="primary"
                    text="Create Account"
                    href="/account/signup"
                    icon={<HiOutlineUserPlus />}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
